"use client";

import type { APIHistoricalRatings } from "@rosette/ts/src/business/api/api_schema.js";
import { scaleLinear } from "@visx/scale";
import { Group } from "@visx/group";
import { LinePath, Line, Area } from "@visx/shape";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { ParentSize } from "@visx/responsive";
import { localPoint } from "@visx/event";
import type { PointerEvent } from "react";
import { useState, useCallback, useMemo } from "react";
import { getYMD } from "@rosette/ts/src/util/getYearMonthDayUTC.js";
import { RatingLegend } from "./RatingLegend.js";
import { getRulesetColourByID } from "../../icon/getRulesetColour.js";


const defaultMargin = {
    top: 10,
    right: 20,
    bottom: 40,
    left: 20,
};


function ymdToDays(minYMD: number, ymd: number): number {
    const minYear = Math.floor(minYMD / 10000);
    const minMonth = Math.floor((minYMD % 10000) / 100);
    const minDay = minYMD % 100;

    const year = Math.floor(ymd / 10000);
    const month = Math.floor((ymd % 10000) / 100);
    const day = ymd % 100;

    const minDate = new Date(minYear, minMonth - 1, minDay);
    const currDate = new Date(year, month - 1, day);
    return Math.round((currDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
}


function daysToDate(baseYMD: number, daysOffset: number) {
    // Convert the "baseYMD + daysOffset" back to an actual Date
    // baseYMD is overallEarliestYMD
    const baseYear = Math.floor(baseYMD / 10000);
    const baseMonth = Math.floor((baseYMD % 10000) / 100) - 1; // zero-based
    const baseDay = baseYMD % 100;
    const baseDate = new Date(baseYear, baseMonth, baseDay);
    const result = new Date(baseDate.getTime() + daysOffset * 24 * 60 * 60 * 1000);
    return result;
}


interface HoverDatum {
    rulesetID: string;
    x: number;
    y: number;
    rating: number;
    days: number;
}


interface InternalHistoricalRatingGraphProps {
    className?: string;
    width: number;
    height: number;
    ratings: Record<string, [number, number, number, number, number][]>;
    margin?: { top: number; right: number; bottom: number; left: number };
    overallEarliestYMD: number;
}


export function InternalHistoricalRatingGraph({
    className,
    width,
    height,
    ratings,
    margin = defaultMargin,
    overallEarliestYMD,
}: InternalHistoricalRatingGraphProps) {

    let minDays: number | null = null;
    let maxDays: number = ymdToDays(overallEarliestYMD, getYMD());
    let minRating: number | null = null;
    let maxRating: number | null = null;

    for (const entries of Object.values(ratings)) {
        for (const [_, days, rating, __, maximumRating] of entries) {
            if (minDays === null || days < minDays) {
                minDays = days;
            }
            if (maxDays === null || days > maxDays) {
                maxDays = days;
            }
            if (minRating === null || rating < minRating) {
                minRating = rating;
            }
            if (maxRating === null || maximumRating > maxRating) {
                maxRating = maximumRating;
            }
        }
    }
    if (minDays === null || maxDays === null || minRating === null || maxRating === null)
        throw new Error("No data provided");

    const horizontalLines: number[] = [];
    const horizontalLineRatingStep: number = 1000;
    let horizontalLineRating: number = horizontalLineRatingStep;
    while (horizontalLineRating < maxRating) {
        horizontalLines.push(horizontalLineRating);
        horizontalLineRating += horizontalLineRatingStep;
    }

    const daysScale = useMemo(() => scaleLinear<number>({
        domain: [minDays, maxDays],
        range: [0, width - margin.left - margin.right],
    }), [minDays, maxDays, width, margin]);

    const ratingScale = useMemo(() => scaleLinear<number>({
        domain: [0, maxRating + 100],
        range: [height - margin.top - margin.bottom, 0],
    }), [maxRating, height, margin]);

    const [hoverData, setHoverData] = useState<HoverDatum[] | null>(null);

    const handlePointerMove = useCallback((evt: PointerEvent<SVGElement>) => {
        const point = localPoint(evt);
        if (!point)
            return;

        const days = Math.round(daysScale.invert(point.x - margin.left));
        const rating = Math.round(ratingScale.invert(point.y - margin.top));
        if (days < minDays || days > maxDays) {
            setHoverData(null);
            return;
        }

        const newHoverData: HoverDatum[] = [];
        for (const [rulesetID, data] of Object.entries(ratings)) {
            let leftRating: number | null = null;
            let foundRightPoint: boolean = false;
            for (const [_, sampleDays, sampleRating] of data) {
                if (sampleDays >= days) {
                    foundRightPoint = true;
                }
                if (sampleDays > days)
                    break;

                leftRating = sampleRating;
            }
            if (leftRating === null || !foundRightPoint)
                continue;

            newHoverData.push({
                rulesetID,
                days,
                rating: leftRating,
                x: daysScale(days),
                y: ratingScale(leftRating),
            });
        }
        newHoverData.sort((a, b) => Math.abs(a.rating - rating) - Math.abs(b.rating - rating));
        setHoverData(newHoverData);
    }, [minDays, maxDays, margin, daysScale, ratingScale, ratings, setHoverData]);

    const handlePointerLeave = useCallback(() => {
        setHoverData(null);
    }, [setHoverData]);

    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;
    if (xMax < 10 || yMax < 10)
        return null;

    return (
        <svg
            className={className}
            width={width}
            height={height}
            onPointerMove={handlePointerMove}
            onPointerLeave={handlePointerLeave}>

            <rect
                x={0}
                y={0}
                width={width}
                height={height}
                className="fill-page" />

            <Group
                left={margin.left}
                top={margin.top}>

                {/* Thin vertical line where you are hovering */}
                {hoverData && hoverData.length > 0 && (
                    <Line
                        from={{ x: hoverData[0].x, y: 0 }}
                        to={{ x: hoverData[0].x, y: yMax }}
                        className="stroke-text"
                        strokeWidth={2}
                        strokeDasharray="4,4"
                        pointerEvents="none" />
                )}

                {/* Horizontal lines at fixed interval */}
                {horizontalLines.map(horizontalLineRating => (
                    <Line
                        key={horizontalLineRating}
                        from={{ x: 0, y: ratingScale(horizontalLineRating) }}
                        to={{ x: xMax, y: ratingScale(horizontalLineRating) }}
                        className="stroke-surface"
                        strokeWidth={2}
                        pointerEvents="none" />
                ))}

                {/* The ranges for each ruleset */}
                {Object.entries(ratings).map(([rulesetID, data]) => (
                    <Area
                        key={rulesetID}
                        data={data}
                        x0={([, days]) => daysScale(days)}
                        x1={([, days]) => daysScale(days)}
                        y0={([,,, minRating]) => ratingScale(minRating)}
                        y1={([,,,, maxRating]) => ratingScale(maxRating)}
                        style={{ fill: getRulesetColourByID(rulesetID) }}
                        opacity={0.2} />
                ))}

                {/* The lines for each ruleset */}
                {Object.entries(ratings).map(([rulesetID, data]) => (
                    <LinePath
                        key={rulesetID}
                        data={data}
                        x={([_, days]) => daysScale(days)}
                        y={([_, __, rating]) => ratingScale(rating)}
                        style={{ stroke: getRulesetColourByID(rulesetID) }}
                        strokeWidth={2} />
                ))}

                <text
                    x={0}
                    y={height - margin?.top - 12}
                    textAnchor="start"
                    fontSize={12}
                    className="fill-text">

                    {daysToDate(overallEarliestYMD, minDays).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    })}
                </text>

                <text
                    x={xMax}
                    y={height - margin?.top - 12}
                    textAnchor="end"
                    fontSize={12}
                    className="fill-text">

                    {daysToDate(overallEarliestYMD, maxDays).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    })}
                </text>

                {!!hoverData && hoverData.map((d, index) => (
                    <circle
                        key={d.rulesetID}
                        cx={d.x}
                        cy={d.y}
                        r={index === 0 ? 5 : 4}
                        className="stroke-text-strong"
                        style={{ fill: getRulesetColourByID(d.rulesetID) }}
                        strokeWidth={index === 0 ? 2 : 1}
                        pointerEvents="none" />
                ))}

                {hoverData && hoverData.length > 0 && (() => {
                    const { rulesetID, x, y, days, rating } = hoverData[0];

                    const ruleset = Ruleset.getByID(rulesetID);
                    const date = daysToDate(overallEarliestYMD, days);
                    const dateString = date.toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    });
                    const ratingString = `${rating.toFixed(0)} ${ruleset.getName()} rating`;
                    const characters = Math.max(dateString.length, ratingString.length);

                    // Approximate text measurement.
                    const textPadding = 6;
                    const textWidth = characters * 6;
                    const textHeight = 20;

                    // Position tooltip.
                    let tooltipX = x + 10;
                    let tooltipY = Math.max(0, y - textHeight);

                    if (tooltipX + textWidth + textPadding * 2 > xMax) {
                        tooltipX = x - 10 - (textWidth + textPadding * 2);
                    }
                    if (tooltipY < 0) {
                        tooltipY = 0;
                    }

                    return (
                        <g pointerEvents="none">
                            {/* Tooltip background */}
                            <rect
                                x={tooltipX}
                                y={tooltipY}
                                width={textWidth + textPadding * 2}
                                height={2 * textHeight}
                                className="fill-surface-sunken"
                                rx={4}
                                ry={4} />

                            <text
                                x={tooltipX + textPadding}
                                y={tooltipY + 0.5 * textHeight + 4}
                                className="fill-text-strong"
                                fontSize={12}>

                                {ratingString}
                            </text>

                            <text
                                x={tooltipX + textPadding}
                                y={tooltipY + 1.5 * textHeight + 4}
                                className="fill-text"
                                fontSize={12}>

                                {dateString}
                            </text>
                        </g>
                    );
                })()}
            </Group>
        </svg>
    );
}


interface HistoricalRatingGraphProps {
    className?: string;
    graphClassName?: string;
    latestRatings: Record<string, number>;
    ratings: APIHistoricalRatings;
}


export function HistoricalRatingGraph({
    className,
    graphClassName,
    latestRatings,
    ratings,
}: HistoricalRatingGraphProps) {
    let longestDuration: number | null = null;
    let overallEarliestYMD: number | null = null;
    let overallLatestYMD: number | null = null;

    // Find earliest / latest YMD among all rulesets
    for (const entries of Object.values(ratings.rulesets)) {
        let earliestYMD: number | null = null;
        let latestYMD: number | null = null;

        for (const entry of entries) {
            const yearMonthDay = entry.year_month_day;
            if (earliestYMD === null || yearMonthDay < earliestYMD) {
                earliestYMD = yearMonthDay;
            }
            if (latestYMD === null || yearMonthDay > latestYMD) {
                latestYMD = yearMonthDay;
            }
        }
        if (earliestYMD === null || latestYMD === null)
            continue;

        const duration = latestYMD - earliestYMD + 1;
        if (longestDuration === null || duration > longestDuration) {
            longestDuration = duration;
        }
        if (overallEarliestYMD === null || earliestYMD < overallEarliestYMD) {
            overallEarliestYMD = earliestYMD;
        }
        if (overallLatestYMD === null || latestYMD > overallLatestYMD) {
            overallLatestYMD = latestYMD;
        }
    }

    if (longestDuration === null || overallEarliestYMD === null || overallLatestYMD === null)
        return <></>;
    if (longestDuration < 2)
        return <></>;

    // Convert ratings to a simpler data structure:
    // ratingData[rulesetID] = [ [yearMonthDay, days, rating], ... ]
    const ratingData: Record<string, [number, number, number, number, number][]> = {};

    for (const [rulesetID, entries] of Object.entries(ratings.rulesets)) {
        const data: [number, number, number, number, number][] = [];
        let lastNonZeroIndex: number = -1;

        let lastRatingDays: number | null = null;
        let lastRatings: [number, number, number] | null = null;
        for (const entry of entries) {
            const yearMonthDay = entry.year_month_day;
            const days = ymdToDays(overallEarliestYMD, yearMonthDay);

            // Skip any left-tail of the graph when your rating is zero.
            if (entry.final_rating < 10 && !lastRatings)
                continue;

            let minRating = entry.min_rating;
            let maxRating = entry.max_rating;
            if (lastRatings) {
                const [lastRating] = lastRatings;
                minRating = Math.min(minRating, lastRating);
                maxRating = Math.max(maxRating, lastRating);
            }
            const ratings: [number, number, number] = [
                entry.final_rating, minRating, maxRating,
            ];

            // Insert a point for the previous rating, so we get a step-like line
            // if you don't have any rating change in a day.
            if (lastRatingDays !== null && lastRatings !== null && lastRatingDays < days - 1) {
                const [lastRating] = lastRatings;
                if (lastRatingDays < days - 2) {
                    data.push([yearMonthDay, lastRatingDays + 1, lastRating, lastRating, lastRating]);
                }
                data.push([yearMonthDay, days - 1, lastRating, lastRating, lastRating]);

            } else if (lastRatingDays === null) {
                data.push([yearMonthDay, days - 1, minRating, minRating, minRating]);
            }
            data.push([yearMonthDay, days, ...ratings]);
            if (entry.final_rating >= 10) {
                lastNonZeroIndex = data.length - 1;
            }

            lastRatingDays = days;
            lastRatings = ratings;
        }
        if (lastNonZeroIndex + 1 < data.length) {
            data.splice(lastNonZeroIndex + 1);
        }

        if (data.length > 1) {
            ratingData[rulesetID] = data;
        }
    }
    if (Object.keys(ratingData).length === 0)
        return <></>;

    return (
        <figure className={cn("mt-0 mb-3 items-start", className ?? null)}>
            <div className="flex w-full flex-col items-center">
                <RatingLegend ratings={latestRatings} />

                <div className={cn("h-[10rem] w-full select-none [&_svg]:overflow-hidden [&_svg]:rounded-2xl", graphClassName ?? null)}>
                    <ParentSize>
                        {({ width, height }) => (
                            <InternalHistoricalRatingGraph
                                className={graphClassName}
                                width={width}
                                height={height}
                                ratings={ratingData}
                                overallEarliestYMD={overallEarliestYMD} />
                        )}
                    </ParentSize>
                </div>
            </div>
        </figure>
    );
}
